/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2025 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { videoPlayButtonHoverString, videoPlayButtonString } from "@eisinc/XDL/Icons/VideoPlayButton";
import { xScreen } from "@eisinc/XDL/Utilities";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import styled, { css } from "styled-components";

export const videoWrapperCommonStyle = css`
    position: relative;
    padding-top: 0;
    height: 0;
    overflow: hidden;
    height: 100%;
    iframe,
    object,
    embed {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
`;

export const YoutubeVideoWrapper = styled.div`
    ${videoWrapperCommonStyle}

    width: 543px;
    ${xScreen("lte", "md")} {
        width: 100%;
    }
    /* lite youtube embed styling */
    .yt-lite {
        background-color: ${getColor("common.black")};
        position: relative;
        display: block;
        contain: content;
        background-position: 50%;
        background-size: cover;
        cursor: pointer;
    }
    .yt-lite.lyt-activated::before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAADGCAYAAAAT+OqFAAAAdklEQVQoz42QQQ7AIAgEF/T/D+kbq/RWAlnQyyazA4aoAB4FsBSA/bFjuF1EOL7VbrIrBuusmrt4ZZORfb6ehbWdnRHEIiITaEUKa5EJqUakRSaEYBJSCY2dEstQY7AuxahwXFrvZmWl2rh4JZ07z9dLtesfNj5q0FU3A5ObbwAAAABJRU5ErkJggg==);
        background-position: top;
        background-repeat: repeat-x;
        height: 60px;
        padding-bottom: 50px;
        width: 100%;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite::after {
        content: "";
        display: block;
        padding-bottom: var(--aspect-ratio);
    }
    .yt-lite > iframe {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }
    .yt-lite > .lty-playbtn {
        width: 75px;
        height: 75px;
        cursor: pointer;
        background: url("${videoPlayButtonString}");
        mask-size: 100%;
        background-repeat: no-repeat;
        background-size: 100%;
        border: none;
        z-index: 1;
        opacity: 1;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite:before {
        content: "";
        position: absolute;
        width: 100%;
        height: 100%;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite:hover:before {
        opacity: 0.4;
    }
    .yt-lite:hover > .lty-playbtn {
        width: 80px;
        height: 80px;
        background: url("${videoPlayButtonHoverString}");
    }
    .yt-lite > .lty-playbtn,
    .yt-lite > .lty-playbtn::before {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
    }
    .yt-lite.lyt-activated {
        cursor: unset;
    }
    .yt-lite.lyt-activated::before,
    .yt-lite.lyt-activated > .lty-playbtn {
        opacity: 0;
        pointer-events: none;
    }
    .lty-visually-hidden {
        display: none;
    }
    ${injectCss}
`;

export interface ProductVideoAssetStyles {
    container?: InjectableCss;
}

interface ProductVideoAssetProps {
    videoId: string;
    title?: string;
    description?: string;
    uploadDate?: string;
    duration?: string;
    videoParameters?: string;
    extendedStyles?: ProductVideoAssetStyles;
}

const ProductVideoAsset: React.FC<ProductVideoAssetProps> = ({
    videoId,
    title = "Product Video",
    description,
    uploadDate,
    duration,
    videoParameters = "autoplay=1&mute=1",
    extendedStyles,
}) => {
    return (
        <YoutubeVideoWrapper {...extendedStyles?.container}>
            <LiteYouTubeEmbed id={videoId} title={title} params={videoParameters} />

            <div itemScope itemType="https://schema.org/VideoObject">
                <div itemID="https://www.youtube.com" itemType="https://schema.org/WebSite">
                    <meta itemProp="url" content={`https://www.youtube.com/embed/${videoId}`} />
                    <meta itemProp="contentUrl" content={`https://www.youtube.com/embed/${videoId}`} />
                    <meta itemProp="thumbnailUrl" content={`http://img.youtube.com/vi/${videoId}/hqdefault.jpg`} />
                    <meta itemProp="name" content={title} />
                    <meta itemProp="description" content={description || title} />
                    <meta itemProp="caption" content={description || title} />
                    {uploadDate && <meta itemProp="uploadDate" content={uploadDate} />}
                    {duration && <meta itemProp="duration" content={duration} />}
                </div>
            </div>
        </YoutubeVideoWrapper>
    );
};

export default ProductVideoAsset;
